import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { authService } from '../services/apiService';
import teamsService from '../services/teamsService';
import publicApiService from '../services/publicApiService';
import { setCanPublishToNetworkPlan } from '../store/workspaceStore';
import {
  useUiModalStore,
  setActiveTab,
  setUpgradeModalOpen,
  setPlanModalOpen,
  setAuthModalOpen,
  setAuthModalMode,
  setPendingTeamInviteToken,
  setNetworkImportSlug,
  setPendingNetworkSlug,
} from '../store/uiModalStore';

/**
 * Acts on pending deep-link tokens + billing returns + the network-publish
 * entitlement. The Tauri deep-link *listeners* live in `useDeepLinks` and only
 * set the pending tokens (team-invite / network-import) into uiModalStore; this
 * hook *consumes* them (accepts the invite, opens the import modal, or prompts
 * login first). Also handles the Stripe `?billing=` return and refreshes the
 * `can_publish` flag. Extracted verbatim from App (same effects/deps/position).
 *
 * @param {{ showToast: Function }} deps
 */
export function useDeepLinkActions({ showToast }) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isInitialized = useAuthStore((s) => s.isInitialized);
  const user = useAuthStore((s) => s.user);
  const pendingTeamInviteToken = useUiModalStore((s) => s.pendingTeamInviteToken);
  const networkImportSlug = useUiModalStore((s) => s.networkImportSlug);
  const pendingNetworkSlug = useUiModalStore((s) => s.pendingNetworkSlug);

  // Handle Stripe billing return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const billing = params.get('billing');
    if (!billing) return;

    // Clean up the URL so the param doesn't persist across refreshes
    const clean = window.location.pathname + window.location.hash;
    window.history.replaceState(null, '', clean);

    if (billing === 'success') {
      setUpgradeModalOpen(false);
      setPlanModalOpen(false);
      const planBefore = useAuthStore.getState().user?.plan ?? 'free';
      let attempts = 0;
      const poll = () => {
        authService.getUser()
          .then((updatedUser) => {
            useAuthStore.setState({ user: updatedUser });
            if (updatedUser.plan && updatedUser.plan !== planBefore) {
              showToast('Subscription activated! Welcome to your new plan.', 'success');
            } else if (++attempts < 8) {
              setTimeout(poll, 2000);
            } else {
              // Webhook took too long; still show success since Stripe confirmed checkout
              showToast('Subscription activated! Welcome to your new plan.', 'success');
            }
          })
          .catch(() => { if (++attempts < 8) setTimeout(poll, 2000); });
      };
      setTimeout(poll, 1500);
    } else if (billing === 'cancel') {
      showToast('Checkout cancelled. No changes were made.', 'info');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Accept team invitation from email deep link (apiboo://join-team?token=...)
  useEffect(() => {
    if (!isAuthenticated || !pendingTeamInviteToken || !isInitialized) return;

    const token = pendingTeamInviteToken;
    setPendingTeamInviteToken(null);

    (async () => {
      try {
        const response = await teamsService.acceptInvitation(token);
        showToast(`Joined team "${response.team?.name ?? 'team'}" successfully!`, 'success');
        setActiveTab('teams');
      } catch (e) {
        showToast(e.message || 'Failed to accept team invitation', 'error');
      }
    })();
  }, [isAuthenticated, isInitialized, pendingTeamInviteToken, showToast]);

  useEffect(() => {
    if (!pendingTeamInviteToken || isAuthenticated || !isInitialized) return;
    setAuthModalMode('login');
    setAuthModalOpen(true);
  }, [pendingTeamInviteToken, isAuthenticated, isInitialized]);

  // Network import deep link — prompt login if needed, then open import modal
  useEffect(() => {
    if (!networkImportSlug || !isInitialized) return;
    if (!isAuthenticated) {
      setPendingNetworkSlug(networkImportSlug);
      setNetworkImportSlug(null);
      setAuthModalMode('login');
      setAuthModalOpen(true);
      return;
    }
  }, [networkImportSlug, isAuthenticated, isInitialized]);

  useEffect(() => {
    if (!isAuthenticated || !pendingNetworkSlug || !isInitialized) return;
    setNetworkImportSlug(pendingNetworkSlug);
    setPendingNetworkSlug(null);
  }, [isAuthenticated, pendingNetworkSlug, isInitialized]);

  useEffect(() => {
    if (!isAuthenticated || !isInitialized) {
      setCanPublishToNetworkPlan(false);
      return;
    }
    publicApiService
      .getMyListings()
      .then((data) => setCanPublishToNetworkPlan(Boolean(data?.can_publish)))
      .catch(() => setCanPublishToNetworkPlan(false));
  }, [isAuthenticated, isInitialized, user?.plan]);
}
